<template>
  <!-- Two groups, not flat siblings: a long label can't strand the CTA. -->
  <div
    v-if="isBand"
    class="@container flex flex-wrap items-center justify-between gap-x-6 gap-y-3 py-3"
  >
    <div class="flex min-w-0 flex-1 items-start gap-3">
      <!-- Same "Add-on" wording as every other paywalled surface. -->
      <span
        class="inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full bg-purple-100 px-2.5 text-[10px] font-bold tracking-wider text-purple-900 uppercase sm:h-7"
      >
        <Icon name="sparkles" class="size-2.5" />
        Add-on
      </span>

      <div class="min-w-0">
        <h2
          class="line-clamp-2 font-artex text-base font-medium tracking-tight text-gray-900 sm:text-lg"
        >
          {{ content.headline }}
        </h2>
        <!-- Clamped, not merely shrinkable: `min-w-0` permits shrinking but
             never clips, so long copy would wrap the band to three rows. -->
        <p
          v-if="content.summary"
          class="mt-0.5 line-clamp-2 text-xs text-gray-500"
        >
          {{ content.summary }}
        </p>
      </div>
    </div>

    <div
      class="flex min-w-0 flex-wrap items-center justify-end gap-x-5 gap-y-2"
    >
      <!-- Only the highlighted names, and muted: at this size the full list
           competed with the headline for the first read. Hidden below a
           CONTAINER width of 64rem, not a viewport one: the docked sidepanel
           narrows this column without changing the viewport, and measured at
           1024px the chips stop costing the band an extra row. -->
      <ul class="hidden flex-wrap items-center gap-x-3 gap-y-1.5 @5xl:flex">
        <li
          v-for="feature in bandFeatures"
          :key="feature.name"
          class="flex items-center gap-1.5 text-xs whitespace-nowrap text-gray-500"
        >
          <!-- Darker than `full`/`compact`: 6px glyphs are non-text graphics
               needing 3:1, and purple-500 lands at 2.02:1 on purple-50. -->
          <span
            class="flex size-3.5 shrink-0 items-center justify-center rounded-full text-[8px]"
            :class="
              feature.upcoming
                ? 'bg-purple-50 text-purple-800'
                : 'bg-gray-100 text-gray-600'
            "
          >
            <Icon
              :name="feature.upcoming ? 'plus' : 'check'"
              class="size-1.5"
            />
          </span>
          {{ feature.name }}
        </li>
      </ul>

      <a :href="mailto" :class="INLINE_CTA_CLASS">Talk to sales</a>
    </div>
  </div>

  <div
    v-else
    :class="
      variant === 'full'
        ? 'grid grid-cols-1 items-start gap-12 py-8 pb-6 lg:grid-cols-[1.1fr_1fr]'
        : 'py-6'
    "
  >
    <!-- Pitch -->
    <div>
      <!-- `full` stacks the pill above the headline; `compact` runs them on one
           line so a narrow column doesn't spend a whole row on a label. -->
      <div
        v-if="variant === 'full'"
        class="inline-flex items-center gap-1.5 rounded-full bg-purple-100 px-3 py-1 text-[11px] font-bold tracking-wider text-purple-900 uppercase"
      >
        <Icon name="sparkles" class="size-3" />
        Premium add-on
      </div>
      <h2
        v-if="variant === 'full'"
        class="mt-4 font-artex text-3xl font-medium tracking-tight text-gray-900"
      >
        {{ content.headline }}
      </h2>
      <div v-else class="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <span
          class="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-purple-100 px-2.5 py-0.5 text-[10px] font-bold tracking-wider text-purple-900 uppercase"
        >
          <Icon name="sparkles" class="size-2.5" />
          Add-on
        </span>
        <h2 class="font-artex text-xl font-medium tracking-tight text-gray-900">
          {{ content.headline }}
        </h2>
      </div>
      <p
        class="mt-3 text-sm leading-relaxed text-gray-500"
        :class="variant === 'full' ? 'max-w-xl' : ''"
      >
        {{ content.body }}
      </p>
      <div
        v-if="variant === 'full'"
        class="mt-5 flex flex-wrap items-center gap-3"
      >
        <a :href="mailto" :class="CTA_CLASS">Talk to sales</a>
      </div>
      <div
        v-if="variant === 'full'"
        class="mt-8 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2"
      >
        <div
          v-for="feature in content.features"
          :key="feature.name"
          class="flex gap-2.5"
        >
          <span
            class="mt-2 flex size-6 shrink-0 items-center justify-center rounded-full text-[10px]"
            :class="
              feature.upcoming
                ? 'bg-purple-100 text-purple-700'
                : 'bg-green-100 text-green-700'
            "
          >
            <Icon
              :name="feature.upcoming ? 'plus' : 'check'"
              class="size-2.5"
            />
          </span>
          <div>
            <div class="text-sm font-semibold text-gray-900">
              {{ feature.name }}
            </div>
            <div class="mt-0.5 text-xs leading-relaxed text-gray-500">
              {{ feature.description }}
            </div>
          </div>
        </div>
      </div>

      <!-- Compact: same badge treatment as `full` so the two read as one
           pattern at two sizes, but names only — in a 320-700px column the
           two-line form outweighed both the headline and the preview. -->
      <ul v-else class="mt-5 space-y-2">
        <li
          v-for="feature in content.features"
          :key="feature.name"
          class="flex items-center gap-2.5 text-[13px] font-medium text-gray-900"
        >
          <span
            class="flex size-5 shrink-0 items-center justify-center rounded-full text-[9px]"
            :class="
              feature.upcoming
                ? 'bg-purple-100 text-purple-700'
                : 'bg-green-100 text-green-700'
            "
          >
            <Icon :name="feature.upcoming ? 'plus' : 'check'" class="size-2" />
          </span>
          {{ feature.name }}
        </li>
      </ul>
    </div>

    <!-- Preview, where the feature has a mockup (slot or per-flag registry).
         `full` puts it in the second column; `compact` stacks it beneath the
         pitch, which is what fills a docked sidepanel's height. -->
    <!-- Hidden from assistive tech: the figures inside are fabricated for the
         mockup, and a screen reader would otherwise announce them as this
         org's real data. The visual "Preview" badge conveys that sighted;
         there is no accessible equivalent to read. -->
    <div
      v-if="$slots.preview || previewComponent"
      aria-hidden="true"
      class="relative rounded-lg border border-gray-200 bg-gray-50"
      :class="variant === 'full' ? 'p-5' : 'mt-7 p-4'"
    >
      <div
        class="absolute -top-2.5 right-4 rounded bg-purple-700 px-2 py-0.5 text-[10px] font-bold tracking-wider text-white uppercase"
      >
        Preview
      </div>

      <div
        v-if="variant === 'full'"
        class="mb-2.5 text-[11px] font-semibold tracking-wider text-gray-500 uppercase"
      >
        What you'll get
      </div>

      <slot name="preview">
        <component :is="previewComponent" v-if="previewComponent" />
      </slot>
    </div>

    <!-- Compact only: right-aligned at the foot of the panel, matching the
         house convention for bounded footers (ConfirmationDialog,
         ConnectChannelPanel, the Add Resource steps). `full` keeps its CTA up
         with the body copy instead — its pitch column has no visible edge, so
         a right-aligned button strands itself mid-page next to the preview
         card. Same reason StoresPaywall put it there. -->
    <div v-if="variant === 'compact'" class="mt-6 flex justify-end">
      <a :href="mailto" :class="CTA_CLASS">Talk to sales</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType, computed } from "vue";
import Icon from "@/components/Core/Icon.vue";
import {
  BAND_FEATURE_LIMIT,
  PAYWALL_CONTENT,
  PAYWALL_VARIANTS,
  paywallMailto,
  type PaywallContent,
  type PaywallVariant,
} from "@/services/paywall_content";
import { previewForFlag } from "@/components/Core/Paywall/previews";

const props = defineProps({
  // Flag key (camelCase, as the flags API renders it) used to look up the copy.
  flag: { type: String, required: true },
  // `full` fills a route; `compact` a narrow tall column; `inline` a wide
  // short slot in a page that still works without the feature.
  variant: {
    type: String as PropType<PaywallVariant>,
    default: "full",
    validator: (value: string) =>
      PAYWALL_VARIANTS.includes(value as PaywallVariant),
  },
});

// Every variant states which of the two layouts it renders. Keyed by the union
// so adding a member is a compile error here rather than silently falling into
// the stacked branch.
const LAYOUT_BY_VARIANT: Record<PaywallVariant, "band" | "stacked"> = {
  full: "stacked",
  compact: "stacked",
  inline: "band",
};

const isBand = computed(() => LAYOUT_BY_VARIANT[props.variant] === "band");

// One size down so the CTA sits inside the row's height rather than setting it.
const INLINE_CTA_CLASS =
  "inline-block shrink-0 rounded-md bg-purple-700 px-3.5 py-1.5 text-xs font-semibold text-white transition duration-150 hover:bg-purple-800";

const CTA_CLASS =
  "inline-block rounded-md bg-purple-700 px-5 py-2.5 text-sm font-semibold text-white transition duration-150 hover:bg-purple-800";

const content = computed<PaywallContent>(() => {
  const found = PAYWALL_CONTENT[props.flag];
  if (found) return found;
  // A gated surface with no copy should still make sense rather than render an
  // empty pitch, so fall back to a generic upsell.
  return {
    label: "This feature",
    headline: "This feature is available as an add-on.",
    body: "Talk to us about enabling it for your organization.",
    features: [],
  };
});

const mailto = computed(() => paywallMailto(content.value));

const previewComponent = computed(() => previewForFlag(props.flag));

// Content picks which names via `highlight`; without any, take the first two.
const bandFeatures = computed(() => {
  const highlighted = content.value.features.filter(
    (feature) => feature.highlight,
  );
  const chosen = highlighted.length > 0 ? highlighted : content.value.features;
  return chosen.slice(0, BAND_FEATURE_LIMIT);
});
</script>
